import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ApiEndpoints from './services/ApiEndpoints';
import './Orders.css';

const ORDER_STATUSES = {
  pending: { label: 'Pendiente', color: '#f59e0b', icon: '⏳' },
  confirmed: { label: 'Confirmado', color: '#3b82f6', icon: '✅' },
  preparing: { label: 'Preparando', color: '#8b5cf6', icon: '👨‍🍳' },
  ready: { label: 'Listo para Entregar', color: '#10b981', icon: '📦' },
  delivering: { label: 'En Camino', color: '#f59e0b', icon: '🚚' },
  delivered: { label: 'Entregado', color: '#10b981', icon: '✅' },
  cancelled: { label: 'Cancelado', color: '#ef4444', icon: '❌' }
};

function Orders() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    console.log('🔥 Orders.loadOrders() - Loading from ApiEndpoints');
    try {
      const data = await ApiEndpoints.getOrders();
      
      // Map data from database format to UI format
      const mappedOrders = data.map(order => ({
        id: order.id,
        customer: {
          name: order.customer_name,
          phone: order.customer_phone,
          address: order.customer_address
        },
        items: order.order_items || [],
        total: order.total,
        status: order.status,
        observations: order.observations,
        createdAt: order.created_at,
        created_at: order.created_at,
        delivery: {
          assignedTo: order.delivery_info?.[0]?.assigned_to || '',
          trackingNumber: order.delivery_info?.[0]?.tracking_number || '',
          estimatedDate: order.delivery_info?.[0]?.estimated_date || '',
          estimatedTime: order.delivery_info?.[0]?.estimated_time || '',
          notes: order.delivery_info?.[0]?.notes || ''
        },
        statusHistory: (order.order_status_history || []).map(h => ({
          status: h.status,
          date: h.date,
          note: h.note
        }))
      }));
      
      setOrders(mappedOrders);
    } catch (error) {
      console.error('❌ Error loading orders:', error);
    }
  };

  const filterOrders = useCallback(() => {
    let filtered = [...orders];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.customer?.name?.toLowerCase().includes(term) ||
        order.customer?.phone?.includes(term) ||
        order.id?.toString().includes(term) ||
        order.items?.some(item => item.name?.toLowerCase().includes(term))
      );
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setFilteredOrders(filtered);
  }, [orders, statusFilter, searchTerm]);

  useEffect(() => {
    filterOrders();
  }, [filterOrders]);

  const updateOrderStatus = async (orderId, newStatus, note = '') => {
    try {
      const result = await ApiEndpoints.updateOrderStatus(orderId, newStatus, note);
      if (result) {
        const updatedOrders = orders.map(order => {
          if (order.id === orderId) {
            return {
              ...order,
              status: newStatus,
              statusHistory: [
                ...order.statusHistory,
                {
                  status: newStatus,
                  date: new Date().toISOString(),
                  note: note
                }
              ]
            };
          }
          return order;
        });

        setOrders(updatedOrders);
        
        if (selectedOrder && selectedOrder.id === orderId) {
          const updatedOrder = updatedOrders.find(o => o.id === orderId);
          setSelectedOrder(updatedOrder);
        }
        
        alert('✅ Estado del pedido actualizado');
      }
    } catch (error) {
      console.error('❌ Error updating order status:', error);
      alert('❌ Error al actualizar estado');
    }
  };

  const updateDeliveryInfo = (orderId, deliveryInfo) => {
    const updatedOrders = orders.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          delivery: {
            ...order.delivery,
            ...deliveryInfo
          }
        };
      }
      return order;
    });

    setOrders(updatedOrders);
    
    if (selectedOrder && selectedOrder.id === orderId) {
      const updatedOrder = updatedOrders.find(o => o.id === orderId);
      setSelectedOrder(updatedOrder);
    }
  };

  const saveOrdersToStorage = async () => {
    alert('✅ Los cambios se guardan automáticamente en la base de datos');
  };

  const createBackup = (data, filename) => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `backup_${filename}_${new Date().toISOString().split('T')[0]}.json`);
    linkElement.click();
    
    console.log(`Backup creado: backup_${filename}_${new Date().toISOString().split('T')[0]}.json`);
  };

  const formatPrice = (price) => {
    return "$" + price.toLocaleString('es-CO');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusInfo = ORDER_STATUSES[status] || ORDER_STATUSES.pending;
    return (
      <span 
        className="px-3 py-1 rounded-full text-xs font-medium text-white flex items-center gap-1"
        style={{ backgroundColor: statusInfo.color }}
      >
        {statusInfo.icon} {statusInfo.label}
      </span>
    );
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedOrder(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="https://cdn.abacus.ai/images/ce62aaea-7192-4eab-8134-3539efec6757.png" alt="GiftBox Logo" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Gestión de Pedidos</h1>
              <p className="text-sm text-gray-500">Panel de Administración</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/admin" className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition">
              ← Productos
            </Link>
            <button 
              onClick={loadOrders}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
            >
              🔄 Actualizar Pedidos
            </button>
            <button 
              onClick={() => createBackup(orders, 'orders')}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
            >
              📄 Backup Pedidos
            </button>
            <button 
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
            >
              🚪 Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Pedidos</h3>
            <p className="text-3xl font-bold text-blue-600">{orders.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Pendientes</h3>
            <p className="text-3xl font-bold text-yellow-600">
              {orders.filter(o => o.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">En Camino</h3>
            <p className="text-3xl font-bold text-orange-600">
              {orders.filter(o => o.status === 'delivering').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Entregados</h3>
            <p className="text-3xl font-bold text-green-600">
              {orders.filter(o => o.status === 'delivered').length}
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre, teléfono, ID del pedido..."
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos los estados</option>
                {Object.entries(ORDER_STATUSES).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                Mostrando: <span className="font-bold text-gray-800">{filteredOrders.length}</span> de {orders.length} pedidos
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Pedidos */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-800">Pedidos</h2>
          </div>
          
          {filteredOrders.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay pedidos</h3>
              <p className="text-gray-500">No se encontraron pedidos con los filtros seleccionados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pedido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.id}</div>
                          <div className="text-sm text-gray-500">{order.items.length} productos</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.customer.name}</div>
                          <div className="text-sm text-gray-500">{order.customer.phone}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {formatPrice(order.total)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <button 
                          onClick={() => openOrderDetails(order)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          👁️ Ver Detalles
                        </button>
                        <button 
                          onClick={() => saveOrdersToStorage([order])}
                          className="text-green-600 hover:text-green-900"
                        >
                          📄 Exportar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalles del Pedido */}
      {showDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Detalles del Pedido {selectedOrder.id}</h3>
              <button onClick={closeDetails} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Información del Cliente */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">👤 Información del Cliente</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Nombre</p>
                    <p className="font-medium">{selectedOrder.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="font-medium">{selectedOrder.customer.phone}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">Dirección</p>
                    <p className="font-medium">{selectedOrder.customer.address}</p>
                  </div>
                </div>
              </div>

              {/* Productos */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">📦 Productos</h4>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">Cantidad: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        {item.discounted_price ? (
                          <>
                            <p className="text-sm text-gray-400 line-through">{formatPrice(item.price * item.quantity)}</p>
                            <p className="font-medium text-green-600">{formatPrice(item.discounted_price * item.quantity)}</p>
                          </>
                        ) : (
                          <p className="font-medium">{formatPrice(item.price * item.quantity)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Observaciones */}
              {selectedOrder.observations && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">📝 Observaciones</h4>
                  <p className="bg-gray-50 p-3 rounded-lg">{selectedOrder.observations}</p>
                </div>
              )}

              {/* Gestión del Pedido */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">⚙️ Gestión del Pedido</h4>
                
                {/* Estado del Pedido */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado del Pedido</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(ORDER_STATUSES).map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => updateOrderStatus(selectedOrder.id, key, `Cambiado a ${value.label}`)}
                        className={`p-3 rounded-lg border-2 transition ${
                          selectedOrder.status === key 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-lg">{value.icon}</div>
                        <div className="text-xs font-medium">{value.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Información de Entrega */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Asignado a</label>
                    <input
                      type="text"
                      value={selectedOrder.delivery.assignedTo || ''}
                      onChange={(e) => updateDeliveryInfo(selectedOrder.id, { assignedTo: e.target.value })}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Nombre del repartidor"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número de Seguimiento</label>
                    <input
                      type="text"
                      value={selectedOrder.delivery.trackingNumber || ''}
                      onChange={(e) => updateDeliveryInfo(selectedOrder.id, { trackingNumber: e.target.value })}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Tracking number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Estimada</label>
                    <input
                      type="date"
                      value={selectedOrder.delivery.estimatedDate || ''}
                      onChange={(e) => updateDeliveryInfo(selectedOrder.id, { estimatedDate: e.target.value })}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hora Estimada</label>
                    <input
                      type="time"
                      value={selectedOrder.delivery.estimatedTime || ''}
                      onChange={(e) => updateDeliveryInfo(selectedOrder.id, { estimatedTime: e.target.value })}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notas de Entrega</label>
                  <textarea
                    value={selectedOrder.delivery.notes || ''}
                    onChange={(e) => updateDeliveryInfo(selectedOrder.id, { notes: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    rows="3"
                    placeholder="Notas adicionales sobre la entrega..."
                  />
                </div>
              </div>

              {/* Historial de Estados */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">📜 Historial de Estados</h4>
                <div className="space-y-2">
                  {selectedOrder.statusHistory.map((history, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-lg">{ORDER_STATUSES[history.status].icon}</div>
                      <div className="flex-1">
                        <div className="font-medium">{ORDER_STATUSES[history.status].label}</div>
                        <div className="text-sm text-gray-500">{formatDate(history.date)}</div>
                        {history.note && <div className="text-sm text-gray-600">{history.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Resumen */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-medium text-gray-700">Total del Pedido:</span>
                  <span className="text-2xl font-bold text-green-600">{formatPrice(selectedOrder.total)}</span>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => saveOrdersToStorage([selectedOrder])}
                    className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
                  >
                    📄 Exportar Pedido
                  </button>
                  <button 
                    onClick={() => {
                      const message = `Hola ${selectedOrder.customer.name}, tu pedido ${selectedOrder.id} ha sido actualizado. Estado actual: ${ORDER_STATUSES[selectedOrder.status].label}.`;
                      const encodedMessage = encodeURIComponent(message);
                      window.open(`https://wa.me/${selectedOrder.customer.phone.replace(/\D/g, '')}?text=${encodedMessage}`, '_blank');
                    }}
                    className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition"
                  >
                    📱 Notificar Cliente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;